import type { AnimationTemplateDef } from './types';

// ── Helpers ────────────────────────────────────────────────────────────

/** Seeded PRNG (mulberry32) — deterministic from seed */
function mulberry32(seed: number) {
  return () => {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hexToRgb(hex: string): [number, number, number] {
  const v = parseInt(hex.replace('#', ''), 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}

function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }

function easeInOut(t: number) { return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; }

// ── Templates ──────────────────────────────────────────────────────────

const lightTrail: AnimationTemplateDef = {
  id: 'light-trail',
  name: 'Light Trail',
  category: 'trails',
  params: [
    { key: 'color', label: 'Color', type: 'color', default: '#22d3ee' },
    { key: 'trailLength', label: 'Trail length', type: 'number', default: 0.25, min: 0.05, max: 0.8, step: 0.05 },
    { key: 'size', label: 'Size', type: 'number', default: 0.06, min: 0.01, max: 0.2, step: 0.01 },
    { key: 'direction', label: 'Direction', type: 'select', default: 'ltr', options: [
      { value: 'ltr', label: 'Left → Right' },
      { value: 'rtl', label: 'Right → Left' },
    ]},
    { key: 'loops', label: 'Loops', type: 'number', default: 1, min: 1, max: 5, step: 1 },
  ],
  render(ctx, w, h, progress, params) {
    const { color = '#22d3ee', trailLength = 0.25, size = 0.06, direction = 'ltr', loops = 1 } = params;
    const [r, g, b] = hexToRgb(color as string);
    const loopProgress = (progress * (loops as number)) % 1;
    let headX = loopProgress * (1 + (trailLength as number)) - (trailLength as number);
    if (direction === 'rtl') headX = 1 - headX;

    const headPx = headX * w;
    const trailPx = (trailLength as number) * w * (direction === 'rtl' ? 1 : -1);
    const radius = (size as number) * h;

    // Trail gradient
    const grad = ctx.createLinearGradient(headPx, 0, headPx + trailPx, 0);
    grad.addColorStop(0, `rgba(${r},${g},${b},0.9)`);
    grad.addColorStop(0.4, `rgba(${r},${g},${b},0.3)`);
    grad.addColorStop(1, `rgba(${r},${g},${b},0)`);

    ctx.fillStyle = grad;
    ctx.fillRect(Math.min(headPx, headPx + trailPx), 0, Math.abs(trailPx), h);

    // Glow dot
    const cy = h / 2;
    ctx.save();
    ctx.shadowColor = `rgb(${r},${g},${b})`;
    ctx.shadowBlur = radius * 3;
    ctx.beginPath();
    ctx.arc(headPx, cy, radius, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${r},${g},${b},0.95)`;
    ctx.fill();
    ctx.restore();

    // Core bright dot
    ctx.beginPath();
    ctx.arc(headPx, cy, radius * 0.35, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.fill();
  },
};

const colorWave: AnimationTemplateDef = {
  id: 'color-wave',
  name: 'Color Wave',
  category: 'geometric',
  params: [
    { key: 'color1', label: 'Color 1', type: 'color', default: '#22d3ee' },
    { key: 'color2', label: 'Color 2', type: 'color', default: '#a855f7' },
    { key: 'waves', label: 'Wave count', type: 'number', default: 3, min: 1, max: 8, step: 1 },
    { key: 'speed', label: 'Speed', type: 'number', default: 1, min: 0.5, max: 4, step: 0.5 },
    { key: 'vertical', label: 'Vertical blend', type: 'boolean', default: true },
  ],
  render(ctx, w, h, progress, params) {
    const { color1 = '#22d3ee', color2 = '#a855f7', waves = 3, speed = 1, vertical = true } = params;
    const [r1, g1, b1] = hexToRgb(color1 as string);
    const [r2, g2, b2] = hexToRgb(color2 as string);
    const phase = progress * Math.PI * 2 * (speed as number);

    const cols = Math.max(1, Math.ceil(w / 4));
    for (let i = 0; i < cols; i++) {
      const t = i / cols;
      const wave = (Math.sin(t * Math.PI * 2 * (waves as number) + phase) + 1) / 2;

      const r = Math.round(lerp(r1, r2, wave));
      const g = Math.round(lerp(g1, g2, wave));
      const b = Math.round(lerp(b1, b2, wave));

      if (vertical) {
        const grad = ctx.createLinearGradient(0, 0, 0, h);
        grad.addColorStop(0, `rgba(${r},${g},${b},0.85)`);
        grad.addColorStop(0.5, `rgba(${Math.round(r * 0.7)},${Math.round(g * 0.7)},${Math.round(b * 0.7)},0.6)`);
        grad.addColorStop(1, `rgba(${r},${g},${b},0.85)`);
        ctx.fillStyle = grad;
      } else {
        ctx.fillStyle = `rgba(${r},${g},${b},0.8)`;
      }

      const colW = Math.ceil(w / cols) + 1;
      ctx.fillRect(Math.floor(t * w), 0, colW, h);
    }
  },
};

const particleField: AnimationTemplateDef = {
  id: 'particle-field',
  name: 'Particle Field',
  category: 'particles',
  params: [
    { key: 'color', label: 'Color', type: 'color', default: '#fbbf24' },
    { key: 'count', label: 'Count', type: 'number', default: 120, min: 20, max: 500, step: 10 },
    { key: 'size', label: 'Size', type: 'number', default: 0.008, min: 0.002, max: 0.03, step: 0.002 },
    { key: 'speed', label: 'Speed', type: 'number', default: 1, min: 0.5, max: 3, step: 0.25 },
    { key: 'direction', label: 'Direction', type: 'select', default: 'ltr', options: [
      { value: 'ltr', label: 'Left → Right' },
      { value: 'rtl', label: 'Right → Left' },
    ]},
  ],
  render(ctx, w, h, progress, params) {
    const { color = '#fbbf24', count = 120, size = 0.008, speed = 1, direction = 'ltr' } = params;
    const [r, g, b] = hexToRgb(color as string);
    const rng = mulberry32(42);
    const n = count as number;
    const radius = (size as number) * h;

    for (let i = 0; i < n; i++) {
      const baseX = rng();
      const baseY = rng();
      const speedVar = 0.5 + rng() * 1.0;
      const sizeVar = 0.5 + rng() * 1.0;
      const phase = rng() * Math.PI * 2;
      const alpha = 0.3 + rng() * 0.7;

      const drift = progress * (speed as number) * speedVar;
      const px = direction === 'ltr'
        ? ((baseX + drift) % 1.2) - 0.1
        : ((baseX - drift) % 1.2 + 1.3) % 1.2 - 0.1;
      const py = baseY + Math.sin(progress * Math.PI * 4 + phase) * 0.03;

      ctx.beginPath();
      ctx.arc(px * w, py * h, radius * sizeVar, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
      ctx.fill();
    }
  },
};

const rollingBall: AnimationTemplateDef = {
  id: 'rolling-ball',
  name: 'Rolling Ball',
  category: 'physics',
  params: [
    { key: 'color', label: 'Color', type: 'color', default: '#ef4444' },
    { key: 'size', label: 'Size', type: 'number', default: 0.12, min: 0.04, max: 0.3, step: 0.02 },
    { key: 'speed', label: 'Speed', type: 'number', default: 1, min: 0.5, max: 3, step: 0.25 },
    { key: 'bounce', label: 'Bounce height', type: 'number', default: 0.4, min: 0, max: 0.8, step: 0.1 },
    { key: 'direction', label: 'Direction', type: 'select', default: 'ltr', options: [
      { value: 'ltr', label: 'Left → Right' },
      { value: 'rtl', label: 'Right → Left' },
    ]},
  ],
  render(ctx, w, h, progress, params) {
    const { color = '#ef4444', size = 0.12, speed = 1, bounce = 0.4, direction = 'ltr' } = params;
    const [r, g, b] = hexToRgb(color as string);
    const radius = (size as number) * h * 0.5;

    const t = progress * (speed as number);
    const px = direction === 'ltr'
      ? (t * 1.2 - 0.1) % 1.2 - 0.1
      : 1.1 - ((t * 1.2) % 1.2);

    // Bounce: absolute sine gives a bouncing motion
    const bounceFreq = 3;
    const bounceT = Math.abs(Math.sin(t * Math.PI * bounceFreq));
    const baseY = h - radius;
    const py = baseY - bounceT * (bounce as number) * h;

    // Shadow
    const shadowScale = 1 - bounceT * 0.3;
    ctx.save();
    ctx.globalAlpha = 0.3 * shadowScale;
    ctx.beginPath();
    ctx.ellipse(px * w, h - radius * 0.15, radius * shadowScale, radius * 0.2 * shadowScale, 0, 0, Math.PI * 2);
    ctx.fillStyle = '#000';
    ctx.fill();
    ctx.restore();

    // Ball with gradient
    const grad = ctx.createRadialGradient(px * w - radius * 0.3, py - radius * 0.3, radius * 0.1, px * w, py, radius);
    grad.addColorStop(0, `rgba(${Math.min(255, r + 80)},${Math.min(255, g + 80)},${Math.min(255, b + 80)},1)`);
    grad.addColorStop(0.7, `rgb(${r},${g},${b})`);
    grad.addColorStop(1, `rgba(${Math.round(r * 0.5)},${Math.round(g * 0.5)},${Math.round(b * 0.5)},1)`);

    ctx.beginPath();
    ctx.arc(px * w, py, radius, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();

    // Specular highlight
    ctx.beginPath();
    ctx.arc(px * w - radius * 0.25, py - radius * 0.3, radius * 0.18, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.fill();
  },
};

const pulseRings: AnimationTemplateDef = {
  id: 'pulse-rings',
  name: 'Pulse Rings',
  category: 'geometric',
  params: [
    { key: 'color', label: 'Color', type: 'color', default: '#22d3ee' },
    { key: 'ringCount', label: 'Ring count', type: 'number', default: 5, min: 2, max: 12, step: 1 },
    { key: 'speed', label: 'Speed', type: 'number', default: 1, min: 0.5, max: 3, step: 0.25 },
    { key: 'originX', label: 'Origin X', type: 'number', default: 0.5, min: 0, max: 1, step: 0.05 },
    { key: 'originY', label: 'Origin Y', type: 'number', default: 0.5, min: 0, max: 1, step: 0.05 },
    { key: 'thickness', label: 'Thickness', type: 'number', default: 0.015, min: 0.005, max: 0.05, step: 0.005 },
  ],
  render(ctx, w, h, progress, params) {
    const { color = '#22d3ee', ringCount = 5, speed = 1, originX = 0.5, originY = 0.5, thickness = 0.015 } = params;
    const [r, g, b] = hexToRgb(color as string);
    const cx = (originX as number) * w;
    const cy = (originY as number) * h;
    const maxRadius = Math.sqrt(w * w + h * h);
    const lineWidth = (thickness as number) * h;

    const n = ringCount as number;
    for (let i = 0; i < n; i++) {
      const ringProgress = ((progress * (speed as number)) + i / n) % 1;
      const radius = easeInOut(ringProgress) * maxRadius;
      const alpha = 1 - ringProgress;

      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${r},${g},${b},${alpha * 0.8})`;
      ctx.lineWidth = lineWidth * (1 - ringProgress * 0.5);
      ctx.stroke();
    }
  },
};

// ── Registry ───────────────────────────────────────────────────────────

export const ANIMATION_TEMPLATES: AnimationTemplateDef[] = [
  lightTrail,
  colorWave,
  particleField,
  rollingBall,
  pulseRings,
];

export const TEMPLATE_MAP = Object.fromEntries(ANIMATION_TEMPLATES.map((t) => [t.id, t]));
